/**
 * Utility functions to clear all user-related data on logout
 * This prevents data leakage between user sessions
 */

import { QueryClient } from '@tanstack/react-query';
import { clearStoredEmail } from './tokenUtils';

/**
 * Clear all user-related caches and data
 * Should be called on logout to prevent data leakage
 */
export function clearAllUserData(queryClient: QueryClient): void {
  // Clear React Query cache
  queryClient.clear();

  void import('@/lib/persistence/db').then(({ clearAllOfflineCaches }) => {
    clearAllOfflineCaches().catch(() => {});
  });
  void import('@/lib/persistence/queryPersister').then(({ clearTanstackPersistStorage }) => {
    clearTanstackPersistStorage().catch(() => {});
  });
  void import('@/lib/persistence/readModelDb').then(({ clearAll }) => {
    clearAll().catch(() => {});
  });
  
  // Only proceed if we're in a browser environment
  if (typeof window === 'undefined') return;
  
  // Clear any other localStorage items that might contain user data
  // Keep only non-user-specific items like theme preferences (if needed)
  const keysToKeep: string[] = [
    // Add any keys that should persist across sessions (e.g., theme, language)
  ];
  
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !keysToKeep.includes(key)) {
      // Only remove user-specific keys
      if (
        key.includes('user') ||
        key.includes('auth') ||
        key.includes('token') ||
        key.includes('settings') ||
        key.includes('profile') ||
        key.includes('preferences') ||
        key.includes('privacy') ||
        key.includes('cache')
      ) {
        keysToRemove.push(key);
      }
    }
  }
  
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove localStorage key "${key}":`, error);
    }
  });
  
  // Clear sessionStorage as well
  try {
    sessionStorage.clear();
  } catch (error) {
    console.warn('Failed to clear sessionStorage:', error);
  }
  
  // Clear stored email for biometric login
  clearStoredEmail();
}
